/**
 * The task insight panel's four-segment hours bar (design v8, Area 7 / ADR 16):
 * one number could not distinguish "not staffed" from "not started" from
 * "over-committed" — three different problems with three different actions.
 *
 * Pure segment math, no React (pattern: dropFeedback.ts). Segments partition
 * the drawn bar:
 *   logged        — realized hours (blue-80)
 *   allocated     — staffed but not yet logged (blue-40)
 *   unallocated   — budget nobody is assigned to yet (hatched grey)
 *   over          — work beyond the budget (coral) — ADDITIVE, never a clamp:
 *                   the bar's total grows to budget + over.
 */

export type HoursBarSegmentKind = 'logged' | 'allocated' | 'unallocated' | 'over';

export type HoursBarSegment = { kind: HoursBarSegmentKind; hours: number; pct: number };

/**
 * Logged hours are drawn in the bar but never listed under it: the legend is
 * about how the budget is covered (Allocated / None / Over), and the Actual
 * reading has its own tile.
 */
const NEVER_IN_LEGEND: ReadonlySet<HoursBarSegmentKind> = new Set(['logged']);

/** Legend entries that render at 0 h; the rest appear only when they hold hours. */
const ALWAYS_IN_LEGEND: ReadonlySet<HoursBarSegmentKind> = new Set(['allocated']);

export type HoursBarModel = {
  /** Partition of the drawn bar; zero-hour segments omitted. */
  segments: HoursBarSegment[];
  /** The bar's 100%: budget + over. */
  totalHours: number;
  overHours: number;
  /**
   * The legend rows (v8: the legend carries the hour values — "Allocated 180 h
   * · None 60 h"). Allocated renders even at 0 h — "nobody staffed" is a
   * statement worth making. 'None' (unallocated) and 'over' appear only when
   * they hold hours: a fully staffed task has no "None 0 h" to report, and a
   * task within budget nothing over. Logged never appears here (see
   * NEVER_IN_LEGEND).
   */
  legend: HoursBarSegment[];
};

/** Null when every input is 0 — the caller renders "—", never an empty 0% bar. */
export const buildHoursBar = (input: {
  budgetHours: number;
  allocatedHours: number;
  realizedHours: number;
}): HoursBarModel | null => {
  const budget = Math.max(0, input.budgetHours);
  const allocated = Math.max(0, input.allocatedHours);
  const realized = Math.max(0, input.realizedHours);
  if (budget === 0 && allocated === 0 && realized === 0) return null;

  // The drawn work is whichever is further along; over is what exceeds budget.
  const drawn = Math.max(allocated, realized);
  const over = Math.max(0, drawn - budget);
  const unallocated = Math.max(0, budget - drawn);
  const total = budget + over;

  // The within-budget part of the drawn work splits logged-first; the coral
  // over tail carries whatever exceeds the budget (allocated or logged alike).
  const base = Math.min(drawn, budget);
  const logged = Math.min(realized, base);
  const allocatedNotLogged = Math.max(0, base - logged);

  const parts: { kind: HoursBarSegmentKind; hours: number }[] = [
    { kind: 'logged', hours: logged },
    { kind: 'allocated', hours: allocatedNotLogged },
    { kind: 'unallocated', hours: unallocated },
    { kind: 'over', hours: over },
  ];

  const segments = parts
    .filter((part) => part.hours > 0)
    .map((part) => ({ ...part, pct: (part.hours / total) * 100 }));

  const legend = parts
    .filter((part) => !NEVER_IN_LEGEND.has(part.kind))
    .filter((part) => ALWAYS_IN_LEGEND.has(part.kind) || part.hours > 0)
    .map((part) => ({ ...part, pct: (part.hours / total) * 100 }));

  return { segments, totalHours: total, overHours: over, legend };
};
